import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CajaBanco } from '../types/cajaBanco';
import { CajaBancoService } from '../services/cajaBancoService';

interface CajaBancoState {
  cajasBancos: CajaBanco[];
  loading: boolean;
  error: string | null;
  selectedCajaBanco: CajaBanco | null;
  dialogOpen: boolean;
  permisosDialogOpen: boolean;
  talonariosDialogOpen: boolean;

  fetchCajasBancos: () => Promise<void>;
  saveCajaBanco: (cajaBanco: CajaBanco) => Promise<void>;
  deleteCajaBanco: (id: number) => Promise<void>;
  setSelectedCajaBanco: (cajaBanco: CajaBanco | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  openPermisosDialog: () => void;
  closePermisosDialog: () => void;
  openTalonariosDialog: () => void;
  closeTalonariosDialog: () => void;
  clearError: () => void;
}

export const useCajaBancoStore = create<CajaBancoState>()(
  devtools(
    (set) => ({
      cajasBancos: [],
      loading: false,
      error: null,
      selectedCajaBanco: null,
      dialogOpen: false,
      permisosDialogOpen: false,
      talonariosDialogOpen: false,

      fetchCajasBancos: async () => {
        set({ loading: true, error: null }, false, 'cajasBancos/fetch/start');
        try {
          const cajasBancos = await CajaBancoService.getAll();
          set({ cajasBancos, loading: false }, false, 'cajasBancos/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar cajas y bancos';
          set({ loading: false, error: errorMessage }, false, 'cajasBancos/fetch/error');
        }
      },

      saveCajaBanco: async (cajaBanco: CajaBanco) => {
        set({ loading: true, error: null }, false, 'cajasBancos/save/start');
        try {
          const saved = await CajaBancoService.save(cajaBanco);
          const isUpdate = cajaBanco.idCajaBanco > 0;
          set((state) => ({
            cajasBancos: isUpdate
              ? state.cajasBancos.map(cb => cb.idCajaBanco === saved.idCajaBanco ? saved : cb)
              : [...state.cajasBancos, saved],
            loading: false,
            dialogOpen: false,
            selectedCajaBanco: null,
          }), false, isUpdate ? 'cajasBancos/update/success' : 'cajasBancos/create/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al guardar caja/banco';
          set({ loading: false, error: errorMessage }, false, 'cajasBancos/save/error');
          throw error;
        }
      },

      deleteCajaBanco: async (id: number) => {
        set({ loading: true, error: null }, false, 'cajasBancos/delete/start');
        try {
          await CajaBancoService.delete(id);
          set((state) => ({
            cajasBancos: state.cajasBancos.filter(cb => cb.idCajaBanco !== id),
            loading: false,
          }), false, 'cajasBancos/delete/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar caja/banco';
          set({ loading: false, error: errorMessage }, false, 'cajasBancos/delete/error');
          throw error;
        }
      },

      setSelectedCajaBanco: (cajaBanco) => set({ selectedCajaBanco: cajaBanco }, false, 'cajasBancos/select'),
      openDialog: () => set({ dialogOpen: true }, false, 'cajasBancos/dialog/open'),
      closeDialog: () => set({ dialogOpen: false, selectedCajaBanco: null }, false, 'cajasBancos/dialog/close'),
      openPermisosDialog: () => set({ permisosDialogOpen: true }, false, 'cajasBancos/permisos/open'),
      closePermisosDialog: () => set({ permisosDialogOpen: false }, false, 'cajasBancos/permisos/close'),
      openTalonariosDialog: () => set({ talonariosDialogOpen: true }, false, 'cajasBancos/talonarios/open'),
      closeTalonariosDialog: () => set({ talonariosDialogOpen: false }, false, 'cajasBancos/talonarios/close'),
      clearError: () => set({ error: null }, false, 'cajasBancos/clearError'),
    }),
    { name: 'CajaBancoStore' }
  )
);
